export type VideoPosterFrame = {
  blob: Blob
  width: number
  height: number
  durationSeconds: number
}

export type ExtractVideoPosterFrameOptions = {
  seekSeconds?: number
  mimeType?: 'image/webp' | 'image/jpeg'
  timeoutMs?: number
}

export type ExtractVideoPosterFrame = (
  file: File,
  options?: ExtractVideoPosterFrameOptions,
) => Promise<VideoPosterFrame>

const DEFAULT_TIMEOUT_MS = 10000

export const extractVideoPosterFrame: ExtractVideoPosterFrame = (file, options) => {
  return new Promise<VideoPosterFrame>((resolve, reject) => {
    const url = URL.createObjectURL(file)
    const video = document.createElement('video')
    let settled = false
    let timeoutId: ReturnType<typeof setTimeout> | null = null

    const cleanup = () => {
      if (timeoutId !== null) clearTimeout(timeoutId)
      video.removeEventListener('loadedmetadata', handleLoadedMetadata)
      video.removeEventListener('seeked', handleSeeked)
      video.removeEventListener('error', handleError)
      URL.revokeObjectURL(url)
    }

    const fail = (message: string) => {
      if (settled) return
      settled = true
      cleanup()
      reject(new Error(message))
    }

    const succeed = (frame: VideoPosterFrame) => {
      if (settled) return
      settled = true
      cleanup()
      resolve(frame)
    }

    const handleLoadedMetadata = () => {
      const duration = video.duration
      if (
        video.videoWidth === 0 ||
        video.videoHeight === 0 ||
        !Number.isFinite(duration) ||
        duration === 0
      ) {
        fail('Video could not be decoded')
        return
      }
      const seekTo = options?.seekSeconds ?? Math.min(1.0, (duration || 2) / 2)
      video.currentTime = seekTo
    }

    const handleSeeked = () => {
      const canvas = document.createElement('canvas')
      canvas.width = video.videoWidth
      canvas.height = video.videoHeight
      const ctx = canvas.getContext('2d')
      if (!ctx) {
        fail('Canvas context unavailable')
        return
      }
      ctx.drawImage(video, 0, 0)
      const mimeType = options?.mimeType ?? 'image/webp'
      canvas.toBlob(
        (blob) => {
          if (!blob) {
            fail('Poster frame could not be encoded')
            return
          }
          succeed({
            blob,
            width: video.videoWidth,
            height: video.videoHeight,
            durationSeconds: Math.round(video.duration),
          })
        },
        mimeType,
        0.85,
      )
    }

    const handleError = () => {
      fail('Video could not be decoded')
    }

    video.addEventListener('loadedmetadata', handleLoadedMetadata)
    video.addEventListener('seeked', handleSeeked)
    video.addEventListener('error', handleError)

    timeoutId = setTimeout(() => {
      fail('Poster frame generation timed out')
    }, options?.timeoutMs ?? DEFAULT_TIMEOUT_MS)

    video.preload = 'metadata'
    video.muted = true
    video.playsInline = true
    video.src = url
  })
}
